import { useEffect } from 'react';
import { usePrevious } from 'react-use';
import useTranslation from 'next-translate/useTranslation';

import { useAtomValue } from 'jotai';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';

import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { DMConversation } from '@/components/pages/conversations/SocialInbox/types';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import { EmptyState } from '@/components/base/EmptyState';
import {
    SocialInboxChatHeader,
    SocialInboxMessages
} from '@/components/pages/conversations/SocialInbox';

interface SocialInboxChatViewProps {
    className?: string;
}

export const SocialInboxChatView = ({ className }: SocialInboxChatViewProps) => {
    const { t } = useTranslation('conversations');

    const openedMessage = useAtomValue(openedMessageAtom) as DMConversation;
    const { activeSocialProfile } = useActiveSocialProfile();
    const previousOpenedMessage = usePrevious(openedMessage);
    const { mutateMessages } = useSocialInboxDataSource();

    const markConversationAsRead = async () => {
        if (
            !openedMessage ||
            openedMessage?.seen_at ||
            previousOpenedMessage?.id === openedMessage?.id ||
            !openedMessage.meta_conversation_id
        ) {
            return;
        }

        const ids = [openedMessage.meta_conversation_id];

        DirectMessageService.mutateConversationReadStatus(mutateMessages, ids, false);

        const isSuccess = await DirectMessageService.toggleConversationReadUnread(
            activeSocialProfile?.id,
            ids,
            false
        );

        if (!isSuccess) {
            DirectMessageService.mutateConversationReadStatus(mutateMessages, ids, true);
        }
    };

    useEffect(() => {
        markConversationAsRead();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openedMessage]);

    return (
        <div
            data-testid="social-inbox-chat-view"
            className={`h-full bg-white dark:bg-icoDarkMode-deepBlack ${className}`}>
            {openedMessage ? (
                <div className="flex h-full flex-col">
                    <SocialInboxChatHeader />
                    <SocialInboxMessages />
                </div>
            ) : (
                <EmptyState
                    icon="bubbles-outline"
                    title={t('social_inbox.no_conversation_title')}
                    description={t('social_inbox.no_conversation_description')}
                    className="h-full"
                />
            )}
        </div>
    );
};
